import {
  OwnInvitationActionError,
  type OwnInvitationActionSuccess,
  readOwnInvitationActionResult
} from '@/lib/invitations/own-invitation-actions'
import {
  InvitableWorkspaceRole,
  WorkspaceInvitation,
  WorkspaceMember,
  WorkspaceTeam,
  WorkspaceInvitationWithDetails,
  WorkspaceRole,
  WorkspaceInvitationStatus
} from '@/types/workspace-invitations'
import type { NexusWorkspaceAccess } from '@/types/nexus-workspace-access'
import { toast } from 'sonner'

type WorkspaceInvitationRow = {
  id: string
  workspace_id: string
  email: string
  role: WorkspaceRole
  custom_message?: string | null
  status: WorkspaceInvitationStatus
  invited_by: string
  created_at: string
  accepted_at?: string | null
  expires_at: string
  accepted_by?: string | null
}

type WorkspaceInvitationWithDetailsRow = WorkspaceInvitationRow & {
  workspace?: {
    id: string
    name: string
    description?: string | null
  } | null
  invited_by_profile?: {
    display_name?: string | null
    email?: string | null
  } | null
}

const parseOwnInvitationAction = async (
  response: Response,
  fallbackMessage: string
): Promise<OwnInvitationActionSuccess> => {
  const payload = readOwnInvitationActionResult(
    await response.json().catch(() => null)
  )

  if (payload?.ok) {
    return payload
  }

  if (payload && !payload.ok) {
    throw new OwnInvitationActionError(payload.code, payload.message)
  }

  throw new Error(fallbackMessage)
}

export class WorkspaceInvitationService {
  private static instance: WorkspaceInvitationService

  static getInstance(): WorkspaceInvitationService {
    if (!WorkspaceInvitationService.instance) {
      WorkspaceInvitationService.instance = new WorkspaceInvitationService()
    }
    return WorkspaceInvitationService.instance
  }

  // Create workspace invitation
  async createWorkspaceInvitation(
    workspaceId: string,
    email: string,
    role: InvitableWorkspaceRole = 'guest',
    customMessage?: string,
    expiresInDays: number = 7
  ): Promise<WorkspaceInvitation> {
    try {
      const res = await fetch(`/api/workspaces/${workspaceId}/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          role,
          customMessage,
          expiresInDays
        })
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to send invitation')
      }

      const data = await res.json()
      toast.success('Invitation sent successfully')
      return {
        id: data.invitation.id,
        workspaceId,
        email: data.invitation.email,
        role: data.invitation.role,
        customMessage,
        status: data.invitation.status,
        invitedBy: '',
        invitedAt: new Date(data.invitation.created_at),
        expiresAt: new Date(data.invitation.expires_at)
      }
    } catch (error) {
      console.error('Failed to create workspace invitation:', error)
      toast.error('Failed to send invitation')
      throw error
    }
  }

  // Get workspace members
  async getWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
    try {
      const res = await fetch(`/api/workspaces/${workspaceId}/members`, {
        method: 'GET'
      })

      if (!res.ok) {
        if ([402, 403, 404, 406, 500].includes(res.status)) {
          return []
        }
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to fetch members')
      }

      const data = await res.json()
      return data?.members || []
    } catch (error) {
      console.error('Failed to get workspace members:', error)
      throw error
    }
  }

  // Get pending invitations for a workspace
  async getWorkspaceInvitations(
    workspaceId: string,
    options?: { includeProcessed?: boolean }
  ): Promise<WorkspaceInvitation[]> {
    try {
      const searchParams = new URLSearchParams()
      if (options?.includeProcessed) {
        searchParams.set('includeProcessed', 'true')
      }

      const invitationUrl = searchParams.size
        ? `/api/workspaces/${workspaceId}/invite?${searchParams.toString()}`
        : `/api/workspaces/${workspaceId}/invite`

      const res = await fetch(invitationUrl, {
        method: 'GET'
      })

      if (!res.ok) {
        if ([402, 403, 404, 406, 500].includes(res.status)) {
          return []
        }
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to fetch invitations')
      }

      const data = await res.json()
      const invitations = (data?.invitations || []) as WorkspaceInvitationWithDetailsRow[]

      // Map database fields to expected interface
      return invitations.map((invitation) => ({
        id: invitation.id,
        workspaceId: invitation.workspace_id,
        email: invitation.email,
        role: invitation.role,
        customMessage: invitation.custom_message || undefined,
        status: invitation.status,
        invitedBy: invitation.invited_by,
        invitedAt: new Date(invitation.created_at), // Map created_at to invitedAt
        acceptedAt: invitation.accepted_at ? new Date(invitation.accepted_at) : undefined,
        expiresAt: new Date(invitation.expires_at),
        acceptedBy: invitation.accepted_by || undefined
      }))
    } catch (error) {
      console.error('Failed to get workspace invitations:', error)
      throw error
    }
  }

  // Get workspace permission teams
  async getWorkspaceTeamsWithAvailability(
    workspaceId: string
  ): Promise<{ teams: WorkspaceTeam[]; available: boolean }> {
    try {
      const res = await fetch(`/api/workspaces/${workspaceId}/teams`, {
        method: 'GET'
      })

      if (!res.ok) {
        if ([402, 403, 404, 406, 500].includes(res.status)) {
          return {
            teams: [],
            available: false
          }
        }
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to fetch teams')
      }

      const data = await res.json()
      return {
        teams: data?.teams || [],
        available: true
      }
    } catch (error) {
      console.error('Failed to get workspace teams:', error)
      throw error
    }
  }

  async getWorkspaceTeams(workspaceId: string): Promise<WorkspaceTeam[]> {
    return (await this.getWorkspaceTeamsWithAvailability(workspaceId)).teams
  }

  // Create a workspace permission team
  async createWorkspaceTeam(args: {
    workspaceId: string
    name: string
    description?: string | null
    role: InvitableWorkspaceRole
    permissions?: WorkspaceTeam['permissions']
    memberUserIds?: string[]
  }): Promise<WorkspaceTeam> {
    try {
      const res = await fetch(`/api/workspaces/${args.workspaceId}/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'create',
          name: args.name,
          description: args.description,
          role: args.role,
          permissions: args.permissions,
          memberUserIds: args.memberUserIds ?? []
        })
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to create team')
      }

      const data = await res.json()
      toast.success('Team created')
      return data.team
    } catch (error) {
      console.error('Failed to create workspace team:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to create team')
      throw error
    }
  }

  // Update a workspace permission team
  async updateWorkspaceTeam(args: {
    workspaceId: string
    teamId: string
    name?: string
    description?: string | null
    role?: InvitableWorkspaceRole
    permissions?: WorkspaceTeam['permissions']
  }): Promise<void> {
    try {
      const res = await fetch(`/api/workspaces/${args.workspaceId}/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update',
          teamId: args.teamId,
          name: args.name,
          description: args.description,
          role: args.role,
          permissions: args.permissions
        })
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to update team')
      }

      toast.success('Team updated')
    } catch (error) {
      console.error('Failed to update workspace team:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to update team')
      throw error
    }
  }

  // Replace workspace team members
  async updateWorkspaceTeamMembers(
    workspaceId: string,
    teamId: string,
    memberUserIds: string[]
  ): Promise<void> {
    try {
      const res = await fetch(`/api/workspaces/${workspaceId}/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'set_members',
          teamId,
          memberUserIds
        })
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to update team members')
      }

      toast.success('Team members updated')
    } catch (error) {
      console.error('Failed to update workspace team members:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to update team members')
      throw error
    }
  }

  // Delete a workspace permission team
  async deleteWorkspaceTeam(workspaceId: string, teamId: string): Promise<void> {
    try {
      const res = await fetch(`/api/workspaces/${workspaceId}/teams`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'delete',
          teamId
        })
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to delete team')
      }

      toast.success('Team deleted')
    } catch (error) {
      console.error('Failed to delete workspace team:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to delete team')
      throw error
    }
  }

  // Get user's pending invitations
  async getUserPendingInvitations(): Promise<WorkspaceInvitationWithDetails[]> {
    try {
      const res = await fetch('/api/workspaces/invitations', {
        method: 'GET'
      })

      if (!res.ok) {
        if ([403, 404, 406, 500].includes(res.status)) {
          return []
        }
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to fetch pending invitations')
      }

      const data = await res.json()
      const invitations = (data?.invitations || []) as WorkspaceInvitationWithDetailsRow[]

      // Map database fields to expected interface
      return invitations.map((invitation) => ({
        id: invitation.id,
        workspaceId: invitation.workspace_id,
        email: invitation.email,
        role: invitation.role,
        customMessage: invitation.custom_message || undefined,
        status: invitation.status,
        invitedBy: invitation.invited_by,
        invitedAt: new Date(invitation.created_at), // Map created_at to invitedAt
        acceptedAt: invitation.accepted_at ? new Date(invitation.accepted_at) : undefined,
        expiresAt: new Date(invitation.expires_at),
        acceptedBy: invitation.accepted_by || undefined,
        workspace: {
          id: invitation.workspace?.id || invitation.workspace_id,
          name: invitation.workspace?.name || 'Workspace',
          description: invitation.workspace?.description || undefined
        },
        invitedByUser: {
          id: invitation.invited_by,
          email: invitation.invited_by_profile?.email || '',
          displayName: invitation.invited_by_profile?.display_name || undefined
        }
      }))
    } catch (error) {
      console.error('Failed to get user pending invitations:', error)
      throw error
    }
  }

  // Accept workspace invitation
  async acceptWorkspaceInvitation(
    invitationId: string
  ): Promise<OwnInvitationActionSuccess> {
    try {
      const res = await fetch(`/api/workspaces/invitations/${invitationId}/accept`, {
        method: 'POST'
      })

      return await parseOwnInvitationAction(res, 'Failed to accept invitation')
    } catch (error) {
      console.error('Failed to accept workspace invitation:', error)
      throw error
    }
  }

  // Decline workspace invitation
  async declineWorkspaceInvitation(
    invitationId: string
  ): Promise<OwnInvitationActionSuccess> {
    try {
      const res = await fetch(`/api/workspaces/invitations/${invitationId}/decline`, {
        method: 'POST'
      })

      return await parseOwnInvitationAction(res, 'Failed to decline invitation')
    } catch (error) {
      console.error('Failed to decline workspace invitation:', error)
      throw error
    }
  }

  // Cancel workspace invitation
  async cancelWorkspaceInvitation(invitationId: string): Promise<void> {
    try {
      const res = await fetch(`/api/workspaces/invitations/${invitationId}/cancel`, {
        method: 'POST'
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to cancel invitation')
      }

      toast.success('Invitation cancelled')
    } catch (error) {
      console.error('Failed to cancel workspace invitation:', error)
      toast.error('Failed to cancel invitation')
      throw error
    }
  }

  // Resend workspace invitation
  async resendWorkspaceInvitation(invitationId: string): Promise<void> {
    try {
      const res = await fetch(`/api/workspaces/invitations/${invitationId}/resend`, {
        method: 'POST'
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to resend invitation')
      }

      toast.success('Invitation resent successfully')
    } catch (error) {
      console.error('Failed to resend workspace invitation:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to resend invitation')
      throw error
    }
  }

  // Remove workspace member
  async removeWorkspaceMember(workspaceId: string, userId: string): Promise<void> {
    try {
      const res = await fetch(`/api/workspaces/${workspaceId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'remove_member',
          userId
        })
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to remove member')
      }

      toast.success('Member removed')
    } catch (error) {
      console.error('Failed to remove workspace member:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to remove member')
      throw error
    }
  }

  // Update workspace member role
  async updateWorkspaceMemberRole(
    workspaceId: string,
    userId: string,
    newRole: WorkspaceRole
  ): Promise<void> {
    try {
      const res = await fetch(`/api/workspaces/${workspaceId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update_role',
          userId,
          role: newRole
        })
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to update member role')
      }

      toast.success('Member role updated')
    } catch (error) {
      console.error('Failed to update workspace member role:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to update member role')
      throw error
    }
  }

  // Update workspace member Nexus access
  async updateWorkspaceMemberNexusAccess(
    workspaceId: string,
    userId: string,
    nexusAccess: NexusWorkspaceAccess
  ): Promise<NexusWorkspaceAccess> {
    try {
      const res = await fetch(`/api/workspaces/${workspaceId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update_nexus_access',
          userId,
          nexusAccess
        })
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error(data?.error || 'Failed to update Nexus access')
      }

      const data = await res.json()
      toast.success('Nexus access updated')
      return data.nexusAccess
    } catch (error) {
      console.error('Failed to update workspace member Nexus access:', error)
      toast.error(error instanceof Error ? error.message : 'Failed to update Nexus access')
      throw error
    }
  }
}

export const workspaceInvitationService = WorkspaceInvitationService.getInstance()
